import { Rive, Layout, Fit } from "@rive-app/webgl2"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

export class HeroCoins {
  constructor() {
    this.rive = null
    this.canvas = null
    this.speedProperty = null
    this.baseSpeed = 0.8
    this.currentSpeed = 1
    this.targetSpeed = 1
    this.lastScrollTime = Date.now()
    this.observer = null
    this.isVisible = false
    this.animationFrameId = null
  }

  isMobile() {
    return typeof window !== "undefined" && window.innerWidth <= this.mobileBreakpoint
  }

  init() {
    if (window.riveInstance) {
      window.riveInstance.cleanup()
      window.riveInstance = null
    }

    gsap.registerPlugin(ScrollTrigger)
    this.canvas = document.getElementById("riveHero")
    if (!this.canvas) {
      return false
    }

    this.setupHighDPICanvas()
    this.initializeRive()
    this.setupIntersectionObserver()
    return true
  }

  setupHighDPICanvas() {
    this.canvas.style.width = "100%"
    this.canvas.style.height = "100%"
    this.canvas.style.display = "block"
    this.canvas.style.objectFit = "contain"
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting

          if (this.rive) {
            if (entry.isIntersecting) {
              this.rive.play()
            } else {
              this.rive.pause()
            }
          }
        })
      },
      {
        threshold: 0.1,
        // rootMargin: "50px" 
      }
    )

    this.observer.observe(this.canvas)
  }

  initializeRive() {
    this.rive = new Rive({
      src: "https://cdn.prod.website-files.com/68da6f24db5fedbdab2e7d56/68e1788e590408f8a05d1655_threshold_10.riv",
      canvas: this.canvas,
      layoutScaleFactor: 1,
      artboard: "main",
      stateMachines: "stateMachine",
      autoplay: true,
      autoBind: true,
      useOffscreenRenderer: true,
      layout: new Layout({
        fit: Fit.Contain,
        alignment: "center",
      }),
      onLoad: () => {
        this.rive.resizeDrawingSurfaceToCanvas()

        let vmi = this.rive.viewModelInstance
        this.speedProperty = vmi.number("speed")
        this.speedProperty.value = this.baseSpeed

        this.setupScrollTrigger()
        this.startSpeedLerp()
      },
    })
  }

  setupScrollTrigger() {
    ScrollTrigger.create({
      trigger: "#heroSection",
      start: "top 0%",
      end: "bottom top",
      scrub: 1,
      onUpdate: (self) => {
        const velocity = self.getVelocity()

        if (Math.abs(velocity) > 10) {
          this.lastScrollTime = Date.now()

          const velocityMultiplier = 0.01
          this.targetSpeed = this.baseSpeed + (velocity * velocityMultiplier)

          const minSpeed = -3
          const maxSpeed = 3
          this.targetSpeed = Math.max(minSpeed, Math.min(maxSpeed, this.targetSpeed))
        }
      },
      // markers: true
    })
  }

  startSpeedLerp() {
    const updateSpeed = () => {
      if (!this.speedProperty || !this.isVisible) {
        this.animationFrameId = requestAnimationFrame(updateSpeed)
        return
      }

      const timeSinceLastScroll = Date.now() - this.lastScrollTime
      const idleThreshold = 100

      if (timeSinceLastScroll > idleThreshold) {
        this.targetSpeed = this.baseSpeed
      }

      const lerpFactor = 0.1
      this.currentSpeed += (this.targetSpeed - this.currentSpeed) * lerpFactor

      this.speedProperty.value = this.currentSpeed

      this.animationFrameId = requestAnimationFrame(updateSpeed)
    }

    updateSpeed()
  }

  destroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId)
    }

    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }

    if (this.rive) {
      this.rive.cleanup()
      this.rive = null
    }

    if (window.riveInstance) {
      window.riveInstance = null
    }

    ScrollTrigger.getAll().forEach((st) => st.kill())
  }
}